'use client';
import { tv } from 'tailwind-variants';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { TrackUserView } from '@/app/(main)/_components/track-user-view/track-user-view.component';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/(pages)/_components/search/search.component';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useSearchState } from '@/store/search/search.store';
import { isVaccineSearchMode } from '@/utils/search/search-vaccine.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const attentionToLoseLaboratoryProcedureStyle = tv({
  slots: {
    container: ['data-[state=true]:animate-pulse-smooth', 'h-full', 'flex flex-col', 'p-5'],
  },
});

interface AttentionToLoseLaboratoryProcedureProps {
  selectedProcedures: SelectedProcedures[];
  showSpanAttentionAnimate?: boolean;
  isVaccineSearch?: boolean;
  cleanValue?: () => void;
}

export function AttentionToLoseLaboratoryProcedure({
  selectedProcedures,
  showSpanAttentionAnimate,
  isVaccineSearch,
  cleanValue,
}: AttentionToLoseLaboratoryProcedureProps) {
  const { handleJump: jump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const { container } = attentionToLoseLaboratoryProcedureStyle();

  const isVaccine = isVaccineSearchMode(useSearchState.getState().selectedSearch, isVaccineSearch);
  const modalDescription = isVaccine
    ? 'Ao sair, as vacinas selecionadas serão perdidas.'
    : 'Ao sair, os exames selecionados serão perdidos.';
  const modalSubtitle = isVaccine ? 'Vacinas selecionadas' : 'Exames selecionados';
  const backAndLostExamsButton = isVaccine ? 'Voltar e perder as vacinas' : 'Voltar e perder os exames';
  const keepExamsButton = isVaccine ? 'Manter vacinas' : 'Manter exames';

  function handleJump(key: ProgressiveSearchModalKeys, returning: boolean = false) {
    if (jump) {
      jump({ key, returning });

      if (cleanValue && returning) {
        cleanValue();
      }
    }
  }

  const handleBackAndLoseExams = (): void => {
    handleJump(ProgressiveSearchModalKeys.SEARCH, true);
  };

  return (
    <div
      data-state={showSpanAttentionAnimate}
      className={container()}
      data-testid={DataTestIdEnum.PROCEDURES_MODAL_ATTENTION_TO_LOSE_LABORATORY_PROCEDURE}
    >
      <TrackUserView eventName={AnalyticsEnum.VIEW_ATTENTION_TO_LOSE_LABORATORY_PROCEDURE} />

      <SheetHeader>
        <div className="flex justify-between gap-3 items-start md:items-center">
          <div className="flex gap-2 items-start md:items-center">
            <Icon icon={faTriangleExclamation} size="xl" className="text-green-400 self-center cursor-pointer" />

            <SheetTitle>
              <div className="flex gap-3">
                <span
                  className="font-extrabold text-2xl text-gray-900 text-wrap text-left"
                  data-testid="attention--attention-to-lose-laboratory-procedure"
                >
                  Atenção
                </span>
              </div>
            </SheetTitle>
          </div>
        </div>
      </SheetHeader>
      <div className="flex flex-col justify-between h-full">
        <div className="flex flex-col gap-3">
          <p className="text-gray-800 my-4">{modalDescription}</p>
          <div>
            <p className="text-gray-800 font-bold">{modalSubtitle}</p>

            <ul className="list-bullet space-y-1 ml-2">
              {selectedProcedures.map((procedure) => (
                <li
                  key={procedure.id}
                  data-testid={DataTestIdEnum.PROCEDURES_MODAL_ATTENTION_TO_LOSE_PROCEDURES_LISTED}
                  className="text-gray-800"
                >
                  {procedure.displayName}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <Button
            color="danger"
            data-testid={DataTestIdEnum.PROCEDURES_MODAL_ATTENTION_TO_LOSE_BACK_BUTTON}
            onClick={() => handleBackAndLoseExams()}
          >
            {backAndLostExamsButton}
          </Button>
          <Button
            variant="secondary"
            data-testid={DataTestIdEnum.PROCEDURES_MODAL_ATTENTION_TO_LOSE_KEEP_BUTTON}
            onClick={() => handleJump(ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS)}
          >
            {keepExamsButton}
          </Button>
        </div>
      </div>
    </div>
  );
}
